// ── THE PRODUCER'S OWN PROJECTION ONTO ITS ANSWER ───────────────────────────
//
// #4777 amendment 1. Two declarations a governed producer makes ABOUT ITS OWN
// RESULT, normalized once here so no composition seam has to invent them.
//
// THE DEFECT THIS CLOSES. `answerShape: 'decision'` on an intent's authored
// contract is a statement about LAYOUT — this answer is a decision, so lead
// with a verdict. It is not, and must never become, a statement about POSTURE:
// how worried to be, how certain the figures are, what legend belongs beside
// them. Composition had those three baked in as adapter defaults lifted from
// one module's plan surface, so every contract-declared decision inherited a
// `watch` tone and a `gated` certainty class no producer had computed. A
// module whose own evaluation said `decision_required` rendered as "watch".
//
// THE RULE. Semantic posture comes from the producer or it is ABSENT. An
// absent posture renders as a neutral banner — the honest state of "this
// producer did not say" — never as a default that happens to look plausible.
//
// Nothing here reads evidence, computes a number, or decides a gate. It
// normalizes two producer declarations into the vocabulary the render contract
// already speaks.

/**
 * The producer's normalized statement of where its own result stands.
 *
 * Deliberately the module monitoring vocabulary minus its render-irrelevant
 * members: this is what the PRODUCER computed, not what a surface wants.
 */
export const GOVERNED_DECISION_POSTURES = [
  'clear',
  'watch',
  'decision_required',
] as const;

export type GovernedDecisionPosture =
  (typeof GOVERNED_DECISION_POSTURES)[number];

/**
 * Verdict-banner tones the workspace render contract accepts.
 *
 * #4779 amendment 2, finding 3. `neutral` is an EXPLICIT member, not the
 * absence of one. Amendment 1 encoded "this producer stated no posture" by
 * omitting `verdictTone` and documented that as a neutral banner — but the
 * renderer reads `verdictTone ?? 'watch'`, so an omitted tone painted the
 * plan lane's amber watch banner over a producer that had said nothing. An
 * absence cannot carry a meaning past a seam that supplies its own default;
 * only a VALUE can. So the neutral state travels as a value, end to end.
 */
export const GOVERNED_VERDICT_TONES = [
  'go',
  'watch',
  'stop',
  'neutral',
] as const;

export type GovernedVerdictTone = (typeof GOVERNED_VERDICT_TONES)[number];

/**
 * The tone for "the producer stated no posture".
 *
 * Named because every seam that has to render an unstated posture must name
 * the same thing — a second spelling is a second meaning waiting to happen.
 */
export const NEUTRAL_GOVERNED_VERDICT_TONE: GovernedVerdictTone = 'neutral';

/**
 * The producer's monitoring warning level as a normalized posture, or `null`.
 *
 * `null` for every value that is NOT a producer statement about the decision:
 * an unrecognised level, and `blocked` (which says the evaluation did not
 * publish, not that the decision is clear). Null is an honest absence and
 * composes as a neutral banner.
 */
export function normalizeGovernedDecisionPosture(
  warningLevel: unknown,
): GovernedDecisionPosture | null {
  if (typeof warningLevel !== 'string') return null;
  switch (warningLevel.trim()) {
    case 'none':
      return 'clear';
    case 'watch':
      return 'watch';
    case 'decision_required':
      return 'decision_required';
    default:
      return null;
  }
}

/**
 * The banner tone a producer posture licenses. TOTAL — every input has a tone.
 *
 * `decision_required` is `stop`: the producer computed that a human has to
 * close something before this proceeds, and softening that to `watch` is the
 * exact misreport this mapping exists to prevent.
 *
 * No posture is `neutral`, EXPLICITLY (amendment 2, finding 3). Returning
 * `null` here made the composer omit the prop, and an omitted prop is not a
 * neutral banner — it is whatever the next seam's fallback happens to be. The
 * honest state of "this producer did not say" is now a value the renderer
 * must consume, so no downstream default can quietly replace it.
 */
export function verdictToneForDecisionPosture(
  posture: GovernedDecisionPosture | null | undefined,
): GovernedVerdictTone {
  switch (posture) {
    case 'clear':
      return 'go';
    case 'watch':
      return 'watch';
    case 'decision_required':
      return 'stop';
    default:
      return NEUTRAL_GOVERNED_VERDICT_TONE;
  }
}

/**
 * A recommendation the PRODUCER authored from its own governed computation.
 *
 * The answer to "so what do we do", owned by the module that computed the
 * result rather than by the narrator. A contract whose
 * `recommendationAuthoritySource` is `producer_scenario` and whose
 * `recommendationPolicy` is `required` must receive one of these — the
 * alternative that shipped was a generic summary sentence and "Executive
 * commentary unavailable" promoted into the executive verdict banner.
 *
 * Both fields are reader-facing prose the producer wrote. Neither is assembled
 * downstream and neither is a template.
 */
export type ProducerAnswerRecommendation = Readonly<{
  /** What the producer recommends be done. */
  statement: string;
  /** The producer's own basis for it, from the figures it computed. */
  basis: string;
}>;

function nonEmptyString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Read a producer recommendation off an untyped producer block, or `null`.
 *
 * A SHAPE read — both fields present and non-empty, or nothing. It classifies
 * no prose, rewrites no words, and substitutes nothing: an absent or partial
 * declaration is an absent recommendation, which the caller must then expose
 * as the gap it is.
 */
export function readProducerAnswerRecommendation(
  value: unknown,
): ProducerAnswerRecommendation | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const statement = nonEmptyString(record.statement);
  const basis = nonEmptyString(record.basis);
  if (!statement || !basis) return null;
  return { statement, basis };
}
